import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../../contexts/UserContext';
import { isHostRole } from '../../utils/roles';
import {
  startLKSession,
  joinLKSession,
  endLKSession,
  lkHeartbeat,
  getLKSessionStatus,
  startLKRecording,
  stopLKRecording,
} from '../../services/livekitService';
import {
  getModulesForStudent,
  getModulesForCourse,
} from '../../services/moduleService';
import { getStudentEnrollments } from '../../services/enrollmentService';
import PatternPulse from '../PatternPulse';
import BreathingBreak from '../BreathingBreak';
import LiveKitVideoArea from './LiveKitVideoArea';
import { buildQuestionPool, LENGTH_OPTIONS, DEFAULT_LENGTH_ID } from '../../data/practiceCatalog';
import '../../styles/classroomLK.css';

// ---------------------------------------------------------------------------
// ClassroomLKPage — parallel to ClassroomPage in App.js but powered by LiveKit.
// Zero Zoom code. Zero changes to ClassroomPage or any Zoom files.
//
// Phase state machine (mirrors ClassroomPage exactly):
//   idle     — teacher: "Start Meeting" | student: polling
//   starting — teacher: API call in flight
//   ready    — student: session live, "Join Now" enabled
//   joining  — LiveKit room connecting
//   live     — in the room
//   ended    — session over
// ---------------------------------------------------------------------------

// ── Inline tool-bar button ──────────────────────────────────────────────────
function ToolBtn({ children, label, active, disabled, onClick }) {
  return (
    <button
      type="button"
      className={`lk-tool-btn${active ? ' lk-tool-btn--active' : ''}`}
      disabled={disabled}
      onClick={onClick}
      title={disabled ? `${label} (available during live session)` : label}
    >
      {children}
      <span className="lk-tool-btn-label">{label}</span>
    </button>
  );
}

export default function ClassroomLKPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();

  const classInfo = location.state?.classInfo || { id: '', title: 'Live Session', detail: '' };
  const missingSession = !classInfo.id;

  // Guest context — set by JoinPage when a demo participant joins without an account
  const isGuest = location.state?.isGuest || false;
  const guestName = location.state?.guestName || 'Guest';

  const isTeacher = currentUser?.role === 'teacher';
  const isHost = isHostRole(currentUser?.role);
  const studentId =
    currentUser?.role === 'student' ? currentUser?.id || currentUser?._id : null;

  // Pre-loaded credentials from JoinPage — skip idle, go straight to joining
  const preloadedToken = location.state?.lkToken || null;
  const preloadedServerUrl = location.state?.lkServerUrl || null;
  const preloadedSessionId = location.state?.sessionId || null;

  const studentParticipant = useMemo(
    () => (classInfo.participants || []).find((p) => p.role === 'student') ?? null,
    [classInfo.participants],
  );
  const mentorParticipant = useMemo(
    () => (classInfo.participants || []).find((p) => p.role === 'mentor') ?? null,
    [classInfo.participants],
  );
  const meetingStudentId = studentParticipant?.userId
    ? String(studentParticipant.userId)
    : null;

  // ── Live clock ────────────────────────────────────────────────────────────
  const [clockTime, setClockTime] = useState('');
  const [clockDate, setClockDate] = useState('');
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setClockTime(now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      setClockDate(now.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // ── Session state ──────────────────────────────────────────────────────────
  const [phase, setPhase] = useState(preloadedToken ? 'joining' : 'idle');
  const [sessionId, setSessionId] = useState(preloadedSessionId);
  const [lkToken, setLkToken] = useState(preloadedToken);
  const [lkServerUrl, setLkServerUrl] = useState(preloadedServerUrl);
  const [message, setMessage] = useState(preloadedToken ? 'Connecting…' : '');
  const [error, setError] = useState('');

  // ── Wellness state ─────────────────────────────────────────────────────────
  const [showPatternPulse, setShowPatternPulse] = useState(false);
  const [showBreathingBreak, setShowBreathingBreak] = useState(false);
  const [showWellnessMenu, setShowWellnessMenu] = useState(false);

  // ── Pattern Pulse data-channel sync ───────────────────────────────────────
  const publishDataRef = useRef(null);
  const [spectatorPPState, setSpectatorPPState] = useState(null);

  const handlePublishReady = useCallback((fn) => {
    publishDataRef.current = fn;
  }, []);

  const handleDataReceived = useCallback((msg) => {
    if (msg?.type === 'pattern_pulse' && isTeacher) {
      setSpectatorPPState(msg);
      setShowPatternPulse(true);
    }
  }, [isTeacher]);

  // Broadcast student game state changes through the data channel
  const handlePPStateChange = useCallback((state) => {
    publishDataRef.current?.({ type: 'pattern_pulse', ...state });
  }, []);

  // ── Recording state ────────────────────────────────────────────────────────
  const [isRecording, setIsRecording] = useState(false);
  const [recordingLoading, setRecordingLoading] = useState(false);

  // ── Whiteboard state ───────────────────────────────────────────────────────
  const [showWhiteboard, setShowWhiteboard] = useState(false);

  // ── Right drawer state: null | 'modules' | 'resources' | 'schedule' ───────
  const [activePanel, setActivePanel] = useState(null);

  // ── Module state (identical to ClassroomPage) ──────────────────────────────
  const [sideModules, setSideModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [modulesError, setModulesError] = useState('');

  const [teacherCourses, setTeacherCourses] = useState([]);
  const [teacherCourseLoading, setTeacherCourseLoading] = useState(false);
  const [teacherCourseError, setTeacherCourseError] = useState('');
  const [selectedTeacherCourseId, setSelectedTeacherCourseId] = useState('');
  const [selectedTeacherModuleId, setSelectedTeacherModuleId] = useState('');
  const [homeworkMessage, setHomeworkMessage] = useState('');

  const [studentSideCourses, setStudentSideCourses] = useState([]);
  const [studentSideCourseId, setStudentSideCourseId] = useState('');

  const defaultHomeworkLength =
    LENGTH_OPTIONS.find((o) => o.id === DEFAULT_LENGTH_ID) || LENGTH_OPTIONS[0];

  // ── Panel helpers ──────────────────────────────────────────────────────────
  const togglePanel = useCallback((panel) => {
    setActivePanel((prev) => (prev === panel ? null : panel));
    setShowWellnessMenu(false);
  }, []);

  // ── Teacher: start a session ───────────────────────────────────────────────
  const handleStart = useCallback(async () => {
    setError('');
    setPhase('starting');
    setMessage('Creating room…');
    try {
      const data = await startLKSession({
        classId: classInfo.id,
        userName: currentUser?.name || 'Teacher',
        userEmail: currentUser?.email || '',
      });
      setSessionId(data.sessionId);
      setLkToken(data.token);
      setLkServerUrl(data.serverUrl);
      setPhase('joining');
      setMessage('Connecting…');
    } catch (err) {
      setError(err.message || 'Failed to start session.');
      setPhase('idle');
    }
  }, [classInfo.id, currentUser]);

  // ── Student: join a live session ───────────────────────────────────────────
  const handleJoin = useCallback(async () => {
    setError('');
    setPhase('joining');
    setMessage('Joining…');
    try {
      const data = await joinLKSession({
        classId: classInfo.id,
        userName: currentUser?.name || 'Student',
        userEmail: currentUser?.email || '',
      });
      setSessionId(data.sessionId);
      setLkToken(data.token);
      setLkServerUrl(data.serverUrl);
    } catch (err) {
      setError(err.message || 'Failed to join session.');
      setPhase('ready');
    }
  }, [classInfo.id, currentUser]);

  // ── LiveKit connected callback — fires when room connection is established ─
  const handleLKConnected = useCallback(() => {
    setPhase('live');
    setMessage('');
  }, []);

  // ── Recording toggle (host only) ───────────────────────────────────────────
  const handleRecordingToggle = useCallback(async () => {
    if (!sessionId || recordingLoading) return;
    setRecordingLoading(true);
    try {
      if (!isRecording) {
        await startLKRecording(sessionId);
        setIsRecording(true);
      } else {
        await stopLKRecording(sessionId);
        setIsRecording(false);
      }
    } catch (err) {
      console.warn('[ClassroomLK] recording toggle failed:', err.message);
    } finally {
      setRecordingLoading(false);
    }
  }, [isRecording, recordingLoading, sessionId]);

  // ── Leave / end ────────────────────────────────────────────────────────────
  const handleLeave = useCallback(async () => {
    setPhase('ended');
    setMessage('Leaving…');
    if (isHost && sessionId) {
      // Stop any active recording before ending — server also auto-stops as fallback
      if (isRecording) {
        stopLKRecording(sessionId).catch(() => {});
      }
      try {
        await endLKSession(sessionId);
      } catch (e) {
        console.warn('[ClassroomLK] endLKSession() best-effort failed:', e.message);
      }
    }
    setLkToken(null);
    setLkServerUrl(null);
    setMessage('Session ended.');
  }, [isHost, isRecording, sessionId]);

  // ── Unexpected disconnect (network drop / room closed by host) ─────────────
  const handleDisconnect = useCallback(() => {
    if (phase === 'live' || phase === 'joining') {
      setPhase('ended');
      setMessage('Disconnected from session.');
    }
  }, [phase]);

  // ── Assign homework (identical to ClassroomPage) ───────────────────────────
  const handleAssignHomework = useCallback(() => {
    if (!selectedTeacherModuleId) {
      setHomeworkMessage('Select a module first.');
      return;
    }
    const selectedModule = sideModules.find(
      (mod) => (mod._id || mod.id) === selectedTeacherModuleId,
    );
    const subjectId = selectedModule?.subjectId || selectedModule?.subject || 'math-geometry';
    const questionPool = buildQuestionPool({
      subjectId,
      moduleIds: [selectedTeacherModuleId],
    });
    if (!questionPool.length) {
      setHomeworkMessage('No question bank entries available for this module yet.');
      return;
    }
    const desired = defaultHomeworkLength?.questions || 12;
    const shuffled = [...questionPool];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    const selection = shuffled.slice(0, Math.min(desired, questionPool.length));
    setHomeworkMessage('');
    navigate('/practice/session', {
      state: {
        quiz: {
          questions: selection,
          generatedAt: new Date().toISOString(),
          subjectId,
          moduleIds: [selectedTeacherModuleId],
          moduleId: selectedTeacherModuleId,
          lengthId: defaultHomeworkLength?.id || DEFAULT_LENGTH_ID,
          requestedCount: desired,
          availableCount: questionPool.length,
        },
      },
    });
  }, [defaultHomeworkLength, navigate, selectedTeacherModuleId, sideModules]);

  // ── Student status polling ─────────────────────────────────────────────────
  useEffect(() => {
    if (isHost || phase !== 'idle' || !classInfo.id) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const status = await getLKSessionStatus(classInfo.id);
        if (cancelled) return;
        if (status.status === 'live') setPhase('ready');
      } catch { /* network hiccup — retry */ }
    };
    poll();
    const interval = setInterval(poll, 10000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isHost, phase, classInfo.id]);

  // ── Teacher heartbeat ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!isHost || phase !== 'live' || !sessionId) return;
    const beat = () =>
      lkHeartbeat(sessionId).catch((err) =>
        console.warn('[ClassroomLK] heartbeat failed:', err?.message, 'sessionId:', sessionId),
      );
    beat(); // fire immediately on connect
    const interval = setInterval(beat, 20000);
    return () => clearInterval(interval);
  }, [isHost, phase, sessionId]);

  // ── sendBeacon on tab close (teacher only) ─────────────────────────────────
  useEffect(() => {
    if (!isHost || phase !== 'live' || !sessionId) return;
    const apiBase = process.env.REACT_APP_LK_API_BASE || '';
    const handleUnload = () => {
      navigator.sendBeacon(
        `${apiBase}/api/lk/end`,
        JSON.stringify({ sessionId }),
      );
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [isHost, phase, sessionId]);

  // ── Teacher course + module loading (skipped for guests) ──────────────────
  useEffect(() => {
    let cancelled = false;
    if (isGuest || !isTeacher) {
      setTeacherCourses([]);
      setTeacherCourseError('');
      setTeacherCourseLoading(false);
      setSelectedTeacherCourseId('');
      return undefined;
    }
    if (!meetingStudentId) {
      setTeacherCourses([]);
      setTeacherCourseLoading(false);
      return undefined;
    }
    setTeacherCourseLoading(true);
    setTeacherCourseError('');
    getStudentEnrollments(meetingStudentId)
      .then((data) => {
        if (cancelled) return;
        const courses = (data?.enrollments || []).map((e) => ({
          _id: String(e.course.id),
          id: String(e.course.id),
          title: e.course.title,
          subject: e.course.subject,
          grade: e.course.grade,
          code: e.course.code,
        }));
        setTeacherCourses(courses);
        setSelectedTeacherCourseId((prev) => prev || courses[0]?.id || '');
      })
      .catch((err) => {
        if (cancelled) return;
        setTeacherCourses([]);
        setTeacherCourseError(err.message || 'Unable to load courses.');
      })
      .finally(() => {
        if (cancelled) return;
        setTeacherCourseLoading(false);
      });
    return () => { cancelled = true; };
  }, [isTeacher, meetingStudentId]);

  useEffect(() => {
    if (isGuest || !isTeacher) return undefined;
    let cancelled = false;
    if (!selectedTeacherCourseId) {
      setSideModules([]);
      setModulesError('');
      setModulesLoading(false);
      setSelectedTeacherModuleId('');
      return undefined;
    }
    setModulesLoading(true);
    setModulesError('');
    getModulesForCourse(selectedTeacherCourseId)
      .then((data) => {
        if (cancelled) return;
        const modules = data?.modules || [];
        setSideModules(modules);
        setSelectedTeacherModuleId((prev) => {
          if (prev && modules.some((m) => (m._id || m.id) === prev)) return prev;
          return modules[0]?._id || modules[0]?.id || '';
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setSideModules([]);
        setModulesError(err.message || 'Unable to load modules.');
        setSelectedTeacherModuleId('');
      })
      .finally(() => {
        if (cancelled) return;
        setModulesLoading(false);
      });
    return () => { cancelled = true; };
  }, [isTeacher, selectedTeacherCourseId]);

  useEffect(() => {
    setHomeworkMessage('');
  }, [selectedTeacherCourseId, selectedTeacherModuleId]);

  // ── Student module loading (skipped for guests) ───────────────────────────
  useEffect(() => {
    if (isGuest || isTeacher) return undefined;
    let cancelled = false;
    if (!studentId) {
      setSideModules([]);
      setModulesError('');
      setModulesLoading(false);
      return undefined;
    }
    setModulesLoading(true);
    setModulesError('');
    getModulesForStudent(studentId)
      .then((data) => {
        if (cancelled) return;
        setSideModules(data?.modules || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setSideModules([]);
        setModulesError(err.message || 'Unable to load modules.');
      })
      .finally(() => {
        if (cancelled) return;
        setModulesLoading(false);
      });
    return () => { cancelled = true; };
  }, [isTeacher, studentId]);

  useEffect(() => {
    let cancelled = false;
    if (isGuest || isTeacher || !studentId) {
      setStudentSideCourses([]);
      setStudentSideCourseId('');
      return undefined;
    }
    getStudentEnrollments(studentId)
      .then((data) => {
        if (cancelled) return;
        const courses = (data?.enrollments || []).map((e) => ({
          id: String(e.course.id),
          title: e.course.title,
          grade: e.course.grade,
        }));
        setStudentSideCourses(courses);
      })
      .catch(() => { if (!cancelled) setStudentSideCourses([]); });
    return () => { cancelled = true; };
  }, [isTeacher, studentId]);

  // ── Render helpers ─────────────────────────────────────────────────────────

  const visibleSideModules = sideModules.filter(
    (mod) => !studentSideCourseId || String(mod.course) === studentSideCourseId,
  );

  const renderOverlay = () => {
    if (phase === 'live') return null;
    return (
      <div className="session-overlay">
        {phase === 'idle' && isHost && (
          <>
            <p className="overlay-label">Ready to begin?</p>
            <button
              type="button"
              className="classroom-action-btn classroom-action-btn--inline"
              onClick={handleStart}
            >
              Start Meeting
            </button>
          </>
        )}
        {phase === 'starting' && (
          <p className="overlay-label">{message || 'Creating room…'}</p>
        )}
        {phase === 'idle' && !isHost && (
          <>
            <p className="overlay-label">Waiting for teacher to start the class…</p>
            <p className="muted-text overlay-sub">
              This page checks automatically every 10 seconds.
            </p>
          </>
        )}
        {phase === 'ready' && !isHost && (
          <>
            <p className="overlay-label">Teacher has started the class.</p>
            <button type="button" className="primary-btn" onClick={handleJoin}>
              Join Now
            </button>
          </>
        )}
        {phase === 'joining' && (
          <p className="overlay-label">{message || 'Connecting…'}</p>
        )}
        {phase === 'ended' && (
          <p className="overlay-label">{message || 'Session ended.'}</p>
        )}
        {error && <p className="error-text overlay-error">{error}</p>}
      </div>
    );
  };

  // ── Drawer panel content ───────────────────────────────────────────────────

  const renderModulesPanel = () => {
    if (isTeacher) {
      return (
        <div className="teacher-module-controls">
          {teacherCourseLoading && <p className="muted-text">Loading courses…</p>}
          {!teacherCourseLoading && teacherCourseError && (
            <p className="error-text">{teacherCourseError}</p>
          )}
          {!teacherCourseLoading && !teacherCourseError && teacherCourses.length === 0 && (
            <p className="muted-text">No courses available yet.</p>
          )}
          {!teacherCourseLoading && !teacherCourseError && teacherCourses.length > 0 && (
            <>
              <div className="side-control-row">
                <label className="eyebrow" htmlFor="lk-teacher-course-select">Course</label>
                <select
                  id="lk-teacher-course-select"
                  className="side-select"
                  value={selectedTeacherCourseId}
                  onChange={(e) => {
                    setSelectedTeacherCourseId(e.target.value);
                    setSelectedTeacherModuleId('');
                  }}
                >
                  {teacherCourses.map((course) => {
                    const cId = course._id || course.id;
                    const sub = course.grade
                      ? `Grade ${course.grade} · ${course.subject || ''}`
                      : course.subject || '';
                    return (
                      <option key={cId} value={cId}>
                        {course.title || course.code || 'Course'}{sub ? ` — ${sub}` : ''}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="side-control-row">
                <label className="eyebrow" htmlFor="lk-teacher-module-select">Module</label>
                <select
                  id="lk-teacher-module-select"
                  className="side-select"
                  value={selectedTeacherModuleId}
                  onChange={(e) => setSelectedTeacherModuleId(e.target.value)}
                  disabled={modulesLoading || sideModules.length === 0}
                >
                  {sideModules.map((mod) => {
                    const mId = mod._id || mod.id;
                    return (
                      <option key={mId} value={mId}>{mod.title}</option>
                    );
                  })}
                </select>
              </div>

              {modulesLoading && <p className="muted-text">Loading modules…</p>}
              {!modulesLoading && modulesError && (
                <p className="error-text">{modulesError}</p>
              )}
              {!modulesLoading && !modulesError && sideModules.length === 0 && (
                <p className="muted-text">No modules for this course yet.</p>
              )}

              <div className="classroom-action-btns">
                <button
                  type="button"
                  className="classroom-action-btn"
                  disabled={!selectedTeacherModuleId}
                  onClick={() => {
                    if (selectedTeacherModuleId) {
                      window.open(`/learn/modules/${selectedTeacherModuleId}`, '_blank');
                    }
                  }}
                >
                  Open Module
                </button>
                <button
                  type="button"
                  className="classroom-action-btn"
                  disabled={!selectedTeacherModuleId}
                  onClick={handleAssignHomework}
                >
                  Assign Quiz
                </button>
              </div>
              {homeworkMessage && <p className="muted-text">{homeworkMessage}</p>}
            </>
          )}
        </div>
      );
    }

    return (
      <div className="side-module-list">
        {studentSideCourses.length > 0 && (
          <div className="side-control-row">
            <label className="eyebrow" htmlFor="lk-student-course-select">Course</label>
            <select
              id="lk-student-course-select"
              className="side-select"
              value={studentSideCourseId}
              onChange={(e) => setStudentSideCourseId(e.target.value)}
            >
              <option value="">All Courses</option>
              {studentSideCourses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title}{course.grade ? ` — Grade ${course.grade}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        {modulesLoading && <p className="muted-text">Loading modules…</p>}
        {!modulesLoading && modulesError && (
          <p className="error-text">{modulesError}</p>
        )}
        {!modulesLoading && !modulesError && visibleSideModules.length === 0 && (
          <p className="muted-text">
            {studentId
              ? 'No modules linked to your courses yet.'
              : 'Enroll in a course to unlock modules.'}
          </p>
        )}
        {!modulesLoading &&
          !modulesError &&
          visibleSideModules.map((mod) => {
            const mId = mod._id || mod.id;
            return (
              <button
                key={mId}
                type="button"
                className="side-module-item"
                onClick={() => window.open(`/learn/modules/${mId}`, '_blank')}
              >
                <div className="side-module-info">
                  <p className="side-module-title">{mod.title}</p>
                  <p className="side-module-meta">
                    {mod.subject || 'General'} · {mod.estimatedMinutes || '—'} min
                  </p>
                </div>
                <svg className="side-module-arrow" viewBox="0 0 16 16" aria-hidden="true">
                  <path
                    d="M6 12l4-4-4-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            );
          })}
      </div>
    );
  };

  const renderResourcesPanel = () => (
    <div className="lk-drawer-section">
      <p className="muted-text lk-drawer-hint">
        {isTeacher
          ? 'Open the resources library and share links or files with your student.'
          : 'Resources shared by your teacher will appear here during the session.'}
      </p>
      <button
        type="button"
        className="classroom-action-btn lk-drawer-action"
        onClick={() => window.open('/resources', '_blank')}
      >
        {isTeacher ? 'Browse Resources Library' : 'View Resources'}
      </button>
    </div>
  );

  const renderSchedulePanel = () => (
    <div className="lk-drawer-section">
      <div className="lk-schedule-card">
        <p className="eyebrow lk-schedule-label">Current Session</p>
        <p className="lk-schedule-title">{classInfo.title}</p>
        {classInfo.detail && (
          <p className="muted-text lk-schedule-detail">{classInfo.detail}</p>
        )}
        <div className={`lk-schedule-badge lk-schedule-badge--${phase}`}>
          {phase === 'live' ? 'Live now' : phase === 'ended' ? 'Ended' : 'Not started'}
        </div>
      </div>
      <button
        type="button"
        className="classroom-action-btn lk-drawer-action"
        onClick={() => navigate('/dashboard')}
      >
        View All Sessions
      </button>
    </div>
  );

  const PANEL_TITLES = {
    modules: 'Learning Modules',
    resources: 'Resources',
    schedule: 'Schedule',
  };

  const showVideoArea = (phase === 'joining' || phase === 'live') && lkToken && lkServerUrl;

  // Callbacks passed to LiveKitVideoArea toolbar
  const handleWhiteboardToggle = useCallback(() => setShowWhiteboard((v) => !v), []);
  const handleWellnessMenuToggle = useCallback(() => {
    setShowWellnessMenu((v) => !v);
    setActivePanel(null);
  }, []);
  const handlePatternPulse = useCallback(() => setShowPatternPulse(true), []);
  const handleBreathingBreak = useCallback(() => setShowBreathingBreak(true), []);

  if (missingSession) {
    return (
      <div className="classroom-stage lk-missing-session">
        <p className="overlay-label">No session selected.</p>
        <p className="muted-text">
          Go to the dashboard and click <strong>Start LK</strong> or <strong>Join LK</strong> on a meeting to enter this room.
        </p>
      </div>
    );
  }

  return (
    <div className="classroom-stage lk-stage">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="lk-session-header">
        {/* Session identity */}
        <div className="lk-session-identity">
          <span className={`lk-live-dot${phase === 'live' ? ' lk-live-dot--on' : ''}`} aria-hidden="true" />
          <div className="lk-session-text">
            <p className="lk-session-eyebrow">Live classroom</p>
            <p className="lk-session-title">{classInfo.title}</p>
          </div>
        </div>

        {/* Clock */}
        <div className="lk-session-clock">
          <p className="lk-clock-time">{clockTime}</p>
          <p className="lk-clock-date">{clockDate}</p>
        </div>

        {/* Participant cards + exit */}
        <div className="lk-session-right">
          {isGuest ? (
            <div className="lk-participant-pill">
              <p className="lk-pill-label">Joining as</p>
              <p className="lk-pill-name">{guestName}</p>
            </div>
          ) : (
            [
              { label: 'Student', participant: studentParticipant },
              { label: 'Mentor', participant: mentorParticipant },
            ].map(({ label, participant }) => (
              <div key={label} className="lk-participant-pill">
                <p className="lk-pill-label">{label}</p>
                <p className="lk-pill-name">{participant?.name ?? '—'}</p>
              </div>
            ))
          )}
          <button
            type="button"
            className="lk-exit-btn"
            onClick={isGuest ? handleLeave : () => navigate('/dashboard')}
          >
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M13 3h3a1 1 0 011 1v12a1 1 0 01-1 1h-3M8 14l-4-4 4-4M4 10h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {isGuest ? 'Leave' : 'Exit'}
          </button>
        </div>
      </header>

      {/* ── Body: video pane + right drawer ────────────────────────────────── */}
      <div className="classroom-body-row">
        {/* Main video pane */}
        <div className="lk-pane">
          {showVideoArea ? (
            <LiveKitVideoArea
              token={lkToken}
              serverUrl={lkServerUrl}
              onConnected={handleLKConnected}
              onDisconnect={handleDisconnect}
              onLeave={handleLeave}
              isHost={isHost}
              isGuest={isGuest}
              showWhiteboard={showWhiteboard}
              onWhiteboardToggle={handleWhiteboardToggle}
              onWhiteboardClose={() => setShowWhiteboard(false)}
              activePanel={activePanel}
              onPanelToggle={togglePanel}
              showWellnessMenu={showWellnessMenu}
              onWellnessMenuToggle={handleWellnessMenuToggle}
              onPatternPulse={handlePatternPulse}
              onBreathingBreak={handleBreathingBreak}
              onPublishReady={handlePublishReady}
              onDataReceived={handleDataReceived}
              isRecording={isRecording}
              recordingLoading={recordingLoading}
              onRecordingToggle={handleRecordingToggle}
            />
          ) : null}
          {renderOverlay()}
        </div>

        {/* Right drawer — slides in when a panel is active */}
        {activePanel && (
          <aside className="lk-drawer">
            <div className="lk-drawer-header">
              <span className="lk-drawer-title">{PANEL_TITLES[activePanel]}</span>
              <button
                type="button"
                className="lk-drawer-close"
                onClick={() => setActivePanel(null)}
                aria-label="Close panel"
              >
                <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <div className="lk-drawer-body">
              {activePanel === 'modules' && renderModulesPanel()}
              {activePanel === 'resources' && renderResourcesPanel()}
              {activePanel === 'schedule' && renderSchedulePanel()}
            </div>
          </aside>
        )}
      </div>

      {/* ── Pre-live tool bar (shown when not in session; the live bar lives inside LiveKitVideoArea) */}
      {!showVideoArea && (
        <div className="lk-toolbar">
          <div className="lk-toolbar-tools">
            {!isGuest && (
              <ToolBtn
                label="Modules"
                active={activePanel === 'modules'}
                onClick={() => window.open('/learn', '_blank')}
              >
                <svg className="lk-tool-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M4 4h7a2 2 0 012 2v9a2 2 0 01-2 2H4V4z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
                  <path d="M13 4h1a2 2 0 012 2v9a2 2 0 01-2 2h-1" stroke="currentColor" strokeWidth="1.4" />
                  <path d="M7 8h4M7 11h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
              </ToolBtn>
            )}
            <ToolBtn
              label="Resources"
              active={activePanel === 'resources'}
              onClick={() => togglePanel('resources')}
            >
              <svg className="lk-tool-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M8 13H5a4 4 0 010-8h3M12 7h3a4 4 0 010 8h-3M7 10h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </ToolBtn>
            <ToolBtn
              label="Schedule"
              active={activePanel === 'schedule'}
              onClick={() => togglePanel('schedule')}
            >
              <svg className="lk-tool-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <rect x="3" y="4" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.4" />
                <path d="M3 8h14M7 2v4M13 2v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                <rect x="6" y="11" width="3" height="3" rx="0.5" fill="currentColor" />
              </svg>
            </ToolBtn>
          </div>
        </div>
      )}

      {showPatternPulse && (
        isTeacher ? (
          <PatternPulse
            onClose={() => { setShowPatternPulse(false); setSpectatorPPState(null); }}
            isSpectator
            spectatorState={spectatorPPState}
            studentName={studentParticipant?.name || 'Student'}
          />
        ) : (
          <PatternPulse
            onClose={() => setShowPatternPulse(false)}
            onStateChange={handlePPStateChange}
          />
        )
      )}
      {showBreathingBreak && <BreathingBreak onClose={() => setShowBreathingBreak(false)} />}
    </div>
  );
}
